import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  LoadingState,
  Modal,
  TextAreaField,
  Toast,
} from './ui';

const RequirementsList = ({ beltId }) => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    notes: '',
    video_url: '',
  });
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const showToast = (message, tone = 'success') => setToast({ open: true, message, tone });

  const fetchRequirements = async () => {
    try {
      const response = await api.get(`/admin/belts/${beltId}/requirements`);
      setRequirements(response.data.data);
    } catch (error) {
      console.error('Error fetching requirements:', error);
      showToast('Error al cargar requisitos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequirements();
  }, [beltId]);

  const openDialog = (item = null) => {
    setSelected(item);
    setFormData(
      item
        ? {
            title: item.title || '',
            description: item.description || '',
            notes: item.notes || '',
            video_url: item.video_url || '',
          }
        : { title: '', description: '', notes: '', video_url: '' }
    );
    setDialogOpen(true);
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('El título es obligatorio', 'error');
      return;
    }
    try {
      if (selected) {
        await api.put(`/admin/belts/${beltId}/requirements/${selected.id}`, formData);
        showToast('Requisito actualizado');
      } else {
        if (requirements.length >= 20) {
          showToast('Límite de requisitos alcanzado (máximo 20)', 'error');
          return;
        }
        await api.post(`/admin/belts/${beltId}/requirements`, formData);
        showToast('Requisito creado');
      }
      setDialogOpen(false);
      fetchRequirements();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar requisito', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/belts/${beltId}/requirements/${confirmId}`);
      setConfirmId(null);
      showToast('Requisito eliminado');
      fetchRequirements();
    } catch (error) {
      showToast('Error al eliminar requisito', 'error');
    }
  };

  const move = async (id, dir) => {
    try {
      await api.put(`/admin/belts/${beltId}/requirements/${id}/move-${dir}`);
      fetchRequirements();
    } catch (error) {
      showToast('Error al mover requisito', 'error');
    }
  };

  if (loading) return <LoadingState label="Cargando requisitos…" />;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Requisitos prácticos ({requirements.length}/20)</h2>
        <button
          type="button"
          className="btn-admin"
          onClick={() => openDialog()}
          disabled={requirements.length >= 20}
        >
          Agregar
        </button>
      </div>

      {requirements.length === 0 ? (
        <EmptyState title="Sin requisitos" description='Haz clic en "Agregar" para comenzar.' />
      ) : (
        <ul className="space-y-3">
          {requirements.map((req, index) => (
            <li key={req.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-white">
                      #{req.requirement_number}
                    </span>
                    <h3 className="font-semibold">{req.title}</h3>
                  </div>
                  {req.description && <p className="mt-2 text-sm text-muted">{req.description}</p>}
                  {req.notes && <p className="mt-1 text-sm italic text-muted">{req.notes}</p>}
                  {req.video_url && (
                    <span className="mt-2 inline-block rounded-full border border-primary/40 px-2 py-0.5 text-xs text-primary">
                      Tiene video
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-1">
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === 0} onClick={() => move(req.id, 'up')}>↑</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === requirements.length - 1} onClick={() => move(req.id, 'down')}>↓</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" onClick={() => openDialog(req)}>Editar</button>
                  <button type="button" className="btn-admin-ghost !min-h-9 text-danger" onClick={() => setConfirmId(req.id)}>Eliminar</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={dialogOpen}
        title={selected ? 'Editar requisito' : 'Agregar requisito'}
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <button type="button" className="btn-admin-secondary" onClick={() => setDialogOpen(false)}>Cancelar</button>
            <button type="submit" form="req-form" className="btn-admin">{selected ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="req-form" className="space-y-4" onSubmit={handleSubmit}>
          <FormField id="title" label="Título" value={formData.title} onChange={handleChange} required />
          <TextAreaField id="description" label="Descripción" value={formData.description} onChange={handleChange} rows={3} />
          <TextAreaField id="notes" label="Notas" value={formData.notes} onChange={handleChange} rows={2} />
          <FormField id="video_url" label="URL del video" value={formData.video_url} onChange={handleChange} placeholder="YouTube o Drive" />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar requisito"
        message="¿Estás seguro de eliminar este requisito?"
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast open={toast.open} message={toast.message} tone={toast.tone} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </div>
  );
};

export default RequirementsList;
